import { api } from '@/app/api';

/**
 * API-обёртка для CRM-зоны: все пути автоматически префиксятся
 * `/crm/:branchId`. Использование:
 *
 *   const crm = crmApi(branchId);
 *   const { data } = await crm.get('/clients', { params: { q: 'иван' } });
 */
export function crmApi(branchId) {
  if (!branchId) throw new Error('branchId is required');
  const base = `/crm/${branchId}`;

  const wrap = (method) => (url, ...rest) =>
    api[method](`${base}${url.startsWith('/') ? url : `/${url}`}`, ...rest);

  return {
    get: wrap('get'),
    post: wrap('post'),
    put: wrap('put'),
    patch: wrap('patch'),
    delete: wrap('delete'),
  };
}

/** Аккуратно вынимает сообщение из axios-ошибки. */
export function errMsg(err, fallback = 'Неизвестная ошибка') {
  return err?.response?.data?.message ?? err?.message ?? fallback;
}

/** Машинный код ошибки (`error` в теле ответа) — по нему различаем сценарии, а не по тексту. */
export function errCode(err) {
  const code = err?.response?.data?.error;
  return typeof code === 'string' ? code : null;
}

/** Данные серверного предупреждения о других клиентах с тем же телефоном. */
export function duplicatePhoneWarning(err) {
  const response = err?.response;
  const details = response?.data?.details;
  if (response?.status !== 409 || details?.reason !== 'phone_exists') return null;
  const branches = Array.isArray(details.branches)
    ? details.branches.filter(Boolean).sort((a, b) => a.localeCompare(b, 'ru'))
    : [];
  const clients = Array.isArray(details.clients)
    ? details.clients
        .filter((item) => item && typeof item.id === 'string')
        .map((item) => ({
          id: item.id,
          fio: typeof item.fio === 'string' && item.fio.trim() ? item.fio.trim() : 'Без имени',
          branches: Array.isArray(item.branches) ? item.branches.filter(Boolean) : [],
          inCurrentBranch: Boolean(item.inCurrentBranch),
        }))
    : [];
  const attachableClients = clients.filter((item) => !item.inCurrentBranch);
  const branchLabel =
    branches.length > 0 ? branches.join(', ') : 'без указанного филиала';
  return {
    reason: 'phone_exists',
    clients,
    attachableClients,
    branches,
    canAttach: attachableClients.length > 0,
    allowCreate: true,
    message: `Клиент с таким номером телефона есть в филиале ${branchLabel}.`,
  };
}

/**
 * Конфликт точного дубля ФИО+телефон.
 * Если клиент в другом филиале — можно предложить привязку к текущему.
 */
export function duplicateIdentityConflict(err) {
  const response = err?.response;
  const details = response?.data?.details;
  if (response?.status !== 409 || details?.reason !== 'same_name_phone') return null;
  const branches = Array.isArray(details.branches)
    ? details.branches.filter(Boolean).sort((a, b) => a.localeCompare(b, 'ru'))
    : [];
  const clientId = typeof details.clientId === 'string' ? details.clientId : null;
  const inCurrentBranch = Boolean(details.inCurrentBranch);
  const branchLabel = branches.length > 0 ? branches.join(', ') : 'другом филиале';
  const fio =
    typeof details.fio === 'string' && details.fio.trim()
      ? details.fio.trim()
      : null;
  const clients =
    clientId && !inCurrentBranch
      ? [
          {
            id: clientId,
            fio: fio || 'Без имени',
            branches,
            inCurrentBranch: false,
          },
        ]
      : [];
  return {
    reason: 'same_name_phone',
    clientId,
    inCurrentBranch,
    branches,
    clients,
    attachableClients: clients,
    message: inCurrentBranch
      ? (response?.data?.message || 'Клиент с таким ФИО и телефоном уже существует')
      : `Клиент с таким ФИО и телефоном уже есть в филиале ${branchLabel}.`,
    canAttach: clients.length > 0,
    allowCreate: false,
  };
}
